import { z } from 'zod'

import ENDPOINTS from '@/config/http/endpoints'
import { parseResponse } from '@/config/http/http.helpers'
import {
  ApiCartDiscountSchema,
  ApiFetchOffersSchema,
  OfferFetchBuyXGetYSchema
} from '@/ducks/offers/offers.schema'
import { rootApi } from '@/ducks/root.api'
import { ApiBaseResponse } from '@/ducks/slice.types'

import { ISubmitOfferBody } from '../offers.types'

export const offersApi = rootApi.injectEndpoints({
  endpoints: build => ({
    fetchOffers: build.query<z.infer<typeof ApiFetchOffersSchema>, void>({
      providesTags: [
        { type: 'Offers', id: 'LIST' },
        { type: 'Menu', id: 'LIST' }
      ],
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_OFFERS
      }),
      transformResponse: (response: z.infer<typeof ApiFetchOffersSchema>) => {
        return parseResponse(response, ApiFetchOffersSchema)
      }
    }),
    disableOffer: build.mutation<void, Array<string>>({
      invalidatesTags: [
        { type: 'Offers', id: 'LIST' },
        { type: 'Menu', id: 'LIST' }
      ],
      query: foodIdsArray => ({
        method: 'DELETE',
        data: { foodIds: foodIdsArray },
        url: ENDPOINTS.DELETE_OFFER
      })
    }),
    submitOffer: build.mutation<ApiBaseResponse, ISubmitOfferBody>({
      invalidatesTags: [{ type: 'Offers', id: 'LIST' }],
      query: submitData => ({
        method: 'POST',
        data: submitData,
        url: ENDPOINTS.SUBMIT_OFFER
      })
    }),
    fetchCartDiscounts: build.query<
      z.infer<typeof ApiCartDiscountSchema>,
      void
    >({
      providesTags: [{ type: 'CartDiscount', id: 'LIST' }],
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_CART_DISCOUNT
      }),
      transformResponse: (response: z.infer<typeof ApiCartDiscountSchema>) => {
        return parseResponse(response, ApiCartDiscountSchema)
      }
    }),
    createCartDiscount: build.mutation<
      { message: string },
      {
        discountType: string
        amountPercentage: number
        maxOff: number
        expirationTime: string
        targetAmount: number
      }
    >({
      invalidatesTags: [{ type: 'CartDiscount', id: 'LIST' }],
      query: discountData => ({
        method: 'POST',
        url: ENDPOINTS.CREAT_CART_DISCOUNT_ENDPOINT,
        data: discountData
      })
    }),
    deleteCartDiscount: build.mutation<
      { status: string; message: string },
      string
    >({
      invalidatesTags: [{ type: 'CartDiscount', id: 'LIST' }],
      query: discountId => ({
        method: 'DELETE',
        url: ENDPOINTS.DELETE_CART_DISCOUNT_ENDPOINT(discountId)
      })
    }),
    editCartDiscount: build.mutation<
      { status: string; message: string },
      {
        discountId: string
        discount: {
          discountType: string
          amountPercentage: number
          maxOff: number
          expirationTime: string
          targetAmount: number
        }
      }
    >({
      invalidatesTags: [{ type: 'CartDiscount', id: 'LIST' }],
      query: ({ discountId, discount }) => ({
        method: 'POST',
        url: ENDPOINTS.EDIT_CART_DISCOUNT_ENDPOINT(discountId),
        data: discount
      })
    }),
    fetchDiscountAmountSuggestions: build.query<
      { suggested_values: number[]; currency_symbol: string },
      void
    >({
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_FLAT_DISCOUNT_SUGGESTIONS
      }),
      transformResponse: (response: {
        suggested_values: number[]
        currency_symbol: string
      }) => {
        return {
          suggested_values: response.suggested_values,
          currency_symbol: response.currency_symbol
        }
      }
    }),
    updateCartDiscount: build.mutation<
      { status: number; message: string },
      {
        status: number
        cartId: string
      }
    >({
      invalidatesTags: [{ type: 'CartDiscount', id: 'LIST' }],
      query: discount => ({
        method: 'PATCH',
        url: ENDPOINTS.UPDATE_CART_DISCOUNT_STATUS_ENDPOINT,
        data: discount
      })
    }),
    fetchOffersBuyXGetY: build.query<
      z.infer<typeof OfferFetchBuyXGetYSchema>,
      void
    >({
      providesTags: [{ type: 'BuyXGetY', id: 'LIST' }],
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_BUYX_GETY_ENDPOINT
      }),
      transformResponse: (response: unknown) => {
        return parseResponse(response, OfferFetchBuyXGetYSchema)
      }
    }),
    deleteOfferBuyXGetY: build.mutation<
      { status: string; message: string },
      string
    >({
      invalidatesTags: [{ type: 'BuyXGetY', id: 'LIST' }],
      query: offerId => ({
        method: 'DELETE',
        url: ENDPOINTS.DELETE_BUYX_GETY_ENDPOINT(offerId)
      })
    }),
    updateBuyXGetY: build.mutation<
      { status: number; message: string },
      {
        status: number
        offerId: string
      }
    >({
      invalidatesTags: [{ type: 'BuyXGetY', id: 'LIST' }],
      query: discount => ({
        method: 'PATCH',
        url: ENDPOINTS.UPDATE_BUYX_GETY_ENDPOINT,
        data: discount
      })
    }),
    createBuyXGetY: build.mutation<
      { message: string },
      {
        itemXId: string
        itemYId: string
        expirationTime: string
        cumulatif: boolean
        discountPercentage: number
        nbrItemsPurchased: number
        nbrItemsOffer: number
      }
    >({
      invalidatesTags: [{ type: 'BuyXGetY', id: 'LIST' }],
      query: offerData => ({
        method: 'POST',
        url: ENDPOINTS.CREATE_BUYX_GETY_ENDPOINT,
        data: offerData
      })
    }),
    editBuyXGetY: build.mutation<
      { status: string; message: string },
      {
        offerId: string
        offer: {
          itemXId: string
          itemYId: string
          expirationTime: string
          cumulatif: boolean
          discountPercentage: number
          nbrItemsPurchased: number
          nbrItemsOffer: number
        }
      }
    >({
      invalidatesTags: [{ type: 'BuyXGetY', id: 'LIST' }],
      query: ({ offerId, offer }) => ({
        method: 'POST',
        url: ENDPOINTS.EDIT_BUYX_GETY_ENDPOINT(offerId),
        data: offer
      })
    })
  })
})

export const {
  useFetchOffersQuery,
  useDisableOfferMutation,
  useSubmitOfferMutation,
  useFetchCartDiscountsQuery,
  useCreateCartDiscountMutation,
  useDeleteCartDiscountMutation,
  useEditCartDiscountMutation,
  useFetchDiscountAmountSuggestionsQuery,
  useUpdateCartDiscountMutation,
  useFetchOffersBuyXGetYQuery,
  useDeleteOfferBuyXGetYMutation,
  useUpdateBuyXGetYMutation,
  useCreateBuyXGetYMutation,
  useEditBuyXGetYMutation
} = offersApi

export default offersApi
